import React from 'react';
import bannerImg from '../../../assets/images/Agal1.png';
import AnimatedCounter from '../../../components/AnimatedCounter';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-dark-50">
      {/* Animated gradient mesh background */}
      <div className="absolute inset-0 bg-hero-mesh" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-grid opacity-50" />

      {/* Animated blobs - reduced on mobile for performance */}
      <div className="hidden sm:block absolute top-20 left-10 w-72 h-72 bg-primary-400 rounded-full blur-[120px] opacity-20 animate-blob" />
      <div className="hidden md:block absolute top-40 right-20 w-96 h-96 bg-accent-purple rounded-full blur-[120px] opacity-15 animate-blob animation-delay-200" />
      <div className="hidden md:block absolute bottom-20 left-1/3 w-80 h-80 bg-accent-cyan rounded-full blur-[120px] opacity-15 animate-blob animation-delay-400" />

      {/* Floating geometric shapes - hidden on mobile for performance */}
      <div className="hidden md:block absolute top-32 right-[15%] w-20 h-20 border border-primary-200 rounded-2xl rotate-12 animate-float opacity-40" />
      <div className="hidden md:block absolute bottom-40 left-[10%] w-16 h-16 bg-gradient-to-br from-primary-400 to-accent-purple rounded-xl rotate-45 animate-float-slow opacity-30" />
      <div className="hidden lg:block absolute top-1/2 right-[8%] w-12 h-12 border-2 border-accent-cyan rounded-full animate-float-slower opacity-30" />

      <div className="container-lg relative z-10 pt-24 pb-12 md:pt-28 md:pb-16 lg:pt-32 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 badge-glow mb-5 md:mb-6 animate-fade-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
              </span>
              <span>Now accepting new projects for Q1 2026</span>
            </div>

            {/* Headline */}
            <h1 className="heading-xl mb-6 animate-fade-up stagger-1" style={{ animationFillMode: 'backwards' }}>
              Software That
              <br />
              <span className="text-gradient">Drives Growth</span>
            </h1>

            {/* Subheadline */}
            <p className="subheading max-w-lg mx-auto lg:mx-0 mb-6 md:mb-8 animate-fade-up stagger-2" style={{ animationFillMode: 'backwards' }}>
              We partner with ambitious companies to design, build, and ship software that solves real problems and delivers measurable results.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8 md:mb-10 animate-fade-up stagger-3" style={{ animationFillMode: 'backwards' }}>
              <a href="#contact" className="btn btn-primary btn-lg group">
                <span className="relative z-10">Start Your Project</span>
                <svg className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a href="#services" className="btn btn-outline btn-lg group">
                See Our Work
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </a>
            </div>

            {/* Social proof */}
            <div className="animate-fade-up stagger-4" style={{ animationFillMode: 'backwards' }}>
              <div className="grid grid-cols-3 gap-4 sm:gap-8 mb-4">
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-dark-900">
                    <AnimatedCounter end={50} suffix="+" duration={2000} />
                  </div>
                  <div className="text-xs sm:text-sm text-dark-500">Projects Shipped</div>
                </div>
                <div className="text-center lg:text-left border-x border-dark-200 px-2 sm:px-4">
                  <div className="text-2xl sm:text-3xl font-bold text-dark-900">
                    <AnimatedCounter end={98} suffix="%" duration={2000} delay={200} />
                  </div>
                  <div className="text-xs sm:text-sm text-dark-500">Client Retention</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-dark-900">
                    <AnimatedCounter end={5} suffix="★" duration={1500} delay={400} />
                  </div>
                  <div className="text-xs sm:text-sm text-dark-500">Average Rating</div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative animate-fade-up stagger-3" style={{ animationFillMode: 'backwards' }}>
            {/* Glow effect behind image */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 via-accent-purple to-accent-cyan rounded-3xl blur-2xl opacity-25 animate-pulse-slow" />

            {/* Decorative frame */}
            <div className="absolute -inset-3 bg-gradient-to-r from-primary-500 via-accent-purple to-accent-cyan rounded-3xl opacity-20" />
            <div className="absolute -inset-2 bg-white rounded-2xl" />

            {/* Main image container */}
            <div className="relative bg-gradient-to-br from-dark-100 to-dark-50 rounded-2xl p-2 shadow-elevated">
              <img
                src={bannerImg}
                alt="Agal Software Solutions - Building innovative software"
                className="w-full h-auto rounded-xl"
              />

              {/* Floating stats card - repositioned on mobile */}
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-2xl shadow-elevated p-3 sm:p-5 animate-float max-w-[200px] sm:max-w-none">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm text-dark-500">Projects Delivered</div>
                    <div className="text-sm sm:text-xl font-bold text-dark-900">On Time & Budget</div>
                  </div>
                </div>
              </div>

              {/* Floating tech badge - hidden on mobile, shown on tablet+ */}
              <div className="hidden sm:block absolute -top-4 -right-4 bg-white rounded-2xl shadow-elevated px-5 py-3 animate-float animation-delay-200">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {['bg-blue-500', 'bg-green-500', 'bg-yellow-500'].map((color, i) => (
                      <div key={i} className={`w-8 h-8 ${color} rounded-full border-2 border-white flex items-center justify-center`}>
                        <span className="text-white text-xs font-bold">{['R', 'N', 'TS'][i]}</span>
                      </div>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-dark-700">Modern Stack</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-dark-300 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-dark-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
